import React, { useEffect, useRef } from 'react';
import { useLocale } from '../contexts/LocaleContext';

const CalendlySection: React.FC = () => {
  const { t } = useLocale();

  // Calendly URL - replace with your real scheduling page path
  const calendlyUrl = t.contact?.calendlyUrl || 'https://calendly.com/your-schedule/30min';
  const widgetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const id = 'calendly-widget-script';
    const initWidget = () => {
      try {
        const Calendly = (window as any).Calendly;
        if (!Calendly || !widgetRef.current) return;
        // Clear previous children to avoid duplicates
        while (widgetRef.current.firstChild) widgetRef.current.removeChild(widgetRef.current.firstChild);
        Calendly.initInlineWidget({ url: calendlyUrl, parentElement: widgetRef.current });
        widgetRef.current.setAttribute('data-calendly-initialized', '1');
      } catch (err) {
        // ignore errors
      }
    };

    const loadScript = () => {
      const existing = document.getElementById(id) as HTMLScriptElement | null;
      if (existing) return;
      const s = document.createElement('script');
      s.id = id;
      s.src = 'https://assets.calendly.com/assets/external/widget.js';
      s.async = true;
      s.onload = () => {
        // try to init once script is loaded
        initWidget();
      };
      document.body.appendChild(s);
    };

    if (!document.getElementById(id)) {
      loadScript();
    } else {
      // script already present, try to init immediately
      initWidget();
    }

    // fallback retry in case Calendly initialization is delayed
    const retry = setTimeout(() => initWidget(), 700);
    // cleanup timer
    return () => clearTimeout(retry);
  }, [calendlyUrl]);

  return (
    <section id="contact" className="py-20">
      {/* full-bleed gradient background */}
      <div className="w-full bg-gradient-primary">
        <div className="flex justify-center">
          <div className="max-w-6xl w-full px-6 md:px-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
          {/* Left: heading + demo points + email (photo style) */}
            <div className="md:pt-6 rounded-2xl text-white pt-3 flex flex-col justify-between">
            <div className='pt-5'>

              <h2 className="md:text-5xl text-4xl font-bold mb-5">{t.contact?.heading}</h2>
              <h3 className="md:text-4xl text-3xl font-semibold mb-8">{t.demo?.title}</h3>
              <ul className="space-y-3 mb-6">
                {(t.demo?.points || []).map((p: string, i: number) => (
                  <li key={i} className="flex items-start gap-5">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/20 text-white mt-1">
                      <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16 6L8.5 13.5L5 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <p className="leading-relaxed md:text-2xl mb-10">{p}</p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="text-md text-center md:pb-8">
              <p className="mb-2">{t.contact?.orReach}</p>
              <a href={`mailto:${t.contact?.email}`} className="font-semibold underline">{t.contact?.email}</a>
            </div>
          </div>

          {/* Right: Calendly embed */}
            <div className="w-full flex justify-center lg:justify-center md:pt-5 pb-3">
              <div className="w-full max-w-[760px] rounded-lg overflow-hidden">
              <div
                ref={widgetRef}
                className="calendly-inline-widget h-[800px] md:h-[920px] w-full"
                data-url={calendlyUrl}
                // Calendly reads the element's computed height; use explicit height (not min-height)
              />
            </div>
          </div>
        </div>
      </div>
        </div>
      </div>
    </section>
  );
};

export default CalendlySection;
